import { type PipelinePhaseDto } from "~/ui-rtk/api/pipelineEventApi"

type PipelineProgressBarProps = {
  phases: PipelinePhaseDto[]
  progressPercent: number
}

type PhaseStatus = PipelinePhaseDto["status"]

function getStatusColor(status: PhaseStatus) {
  switch (status) {
    case "completed":
      return "bg-states-success"
    case "failed":
      return "bg-states-error"
    case "in_progress":
      return "bg-states-info"
    default:
      return "bg-basic-blue"
  }
}

function getStatusTextColor(status: PhaseStatus) {
  switch (status) {
    case "completed":
      return "text-states-success"
    case "failed":
      return "text-states-error"
    case "in_progress":
      return "text-states-info"
    default:
      return "text-basic-grey-inactive"
  }
}

function getStatusLabel(status: PhaseStatus) {
  switch (status) {
    case "completed":
      return "✓ Complete"
    case "failed":
      return "✗ Failed"
    case "in_progress":
      return "◐ In Progress"
    default:
      return "○ Pending"
  }
}

export default function PipelineProgressBar({
  phases,
  progressPercent,
}: PipelineProgressBarProps) {
  return (
    <div className="space-y-2">
      {/* Overall progress bar */}
      <div className="relative h-2 rounded-full bg-basic-blue overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-states-success transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Phase segments */}
      <div className="flex w-full gap-0.5">
        {phases.map(phase => (
          <div
            key={phase.phase}
            className="relative flex-1 h-3 group"
            title={`${phase.label}: ${phase.status}`}
          >
            <div
              className={`h-full rounded-sm transition-colors ${getStatusColor(phase.status)}`}
            />
            {/* Tooltip on hover */}
            <div className="absolute z-10 hidden px-3 py-2 text-xs rounded-md shadow-lg bottom-full left-1/2 -translate-x-1/2 mb-2 bg-basic-dark-blue text-basic-white whitespace-nowrap group-hover:block min-w-[200px]">
              <div className="font-semibold mb-1">{phase.label}</div>
              <div className={`mb-2 ${getStatusTextColor(phase.status)}`}>
                {getStatusLabel(phase.status)}
              </div>
              {/* Event details */}
              <div className="space-y-1 text-basic-grey-inactive">
                {phase.events.map(event => (
                  <div key={event.event} className="flex justify-between">
                    <span>{event.label}</span>
                    <span className={getStatusTextColor(event.status)}>
                      {event.status === "completed" &&
                        `✓${event.successCount || 1}`}
                      {event.status === "failed" && `✗${event.failCount || 1}`}
                      {event.status === "pending" && "○"}
                    </span>
                  </div>
                ))}
              </div>
              {/* Tooltip arrow */}
              <div className="absolute w-2 h-2 rotate-45 -bottom-1 left-1/2 -translate-x-1/2 bg-basic-dark-blue" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function PipelineProgressSummary({
  phases,
  progressPercent,
}: PipelineProgressBarProps) {
  const completedCount = phases.filter(p => p.status === "completed").length
  const failedCount = phases.filter(p => p.status === "failed").length

  return (
    <span className="text-sm text-basic-grey-inactive">
      {progressPercent}%{" · "}
      <span className="text-states-success">{completedCount}</span>
      {failedCount > 0 && (
        <>
          /<span className="text-states-error">{failedCount}</span>
        </>
      )}
      /{phases.length} phases
    </span>
  )
}
